import { useFormikContext } from 'formik';

export type PackageData = {
  size: {
    width: number | null;
    height: number | null;
    depth: number | null;
    unit: string | null;
  };
  weight: number | null;
  notes: string | null;
};

interface FormValues {
  packages: PackageData[];
}

const usePackageFormHelper = (prefixName: string = 'packages') => {
  const formik = useFormikContext<FormValues>();
  const packages = formik.values?.packages ?? [];

  const hasMoreThanOnePackage = packages?.length > 1;

  // Safely get the package at the provided index
  const getPackage = (index: number): PackageData | undefined => {
    if (index < 0 || index >= packages.length) {
      console.warn(`Index ${index} out of bounds for packages array`);
      return undefined;
    }
    return packages[index];
  };

  // Set a package at the provided index
  const setPackage = (index: number, packageData: PackageData) => {
    if (index < 0 || index >= packages.length) {
      console.warn(`Index ${index} out of bounds. Cannot set package.`);
      return;
    }
    packages[index] = packageData;
    formik.setFieldValue(prefixName, packages);
  };

  // Add a new package to the array
  const addNewPackage = () => {
    const packageData: PackageData = {
      size: {
        width: null,
        height: null,
        depth: null,
        unit: null,
      },
      weight: null,
      notes: null,
    };
    const newPackages = [...packages, packageData];
    formik.setTouched({ ...formik.touched, [prefixName]: false });
    formik.setFieldValue(prefixName, newPackages);
  };

  // Delete a package at the provided index
  const deletePackage = (index: number) => {
    if (index < 0 || index >= packages.length) {
      console.warn(`Index ${index} out of bounds. Cannot delete package.`);
      return;
    }
    const updatedPackages = packages.filter((_, i) => i !== index);
    formik.setFieldValue(prefixName, updatedPackages);
  };

  // Helper to check if the packages array is empty
  const hasPackages = (): boolean => {
    return Array.isArray(packages) && packages.length > 0;
  };

  return {
    getPackage,
    setPackage,
    addNewPackage,
    deletePackage,
    hasPackages,
    hasMoreThanOnePackage,
    packages,
  };
};

export default usePackageFormHelper;
